import { useEffect, useRef, useState } from "react";
import type { MutableRefObject } from "react";
import type { RecordingOpenEvent, TimeUpdateEvent, TimelineChangeEvent, WebViewer } from "@rerun-io/web-viewer";
import { RERUN_VIEWER_VERSION } from "../api/types";
import { ErrorState, LoadingState } from "./AsyncState";
import { isMobileOperatingSystem } from "../utils/device";

export interface RerunCursor {
  recordingId: string;
  timeline: string;
  timeNs: string;
}

interface RerunViewerProps {
  rrdUrl: string;
  mobileRrdUrl?: string | null;
  rerunVersion: string;
  viewerVersion?: string;
  timeline?: string;
  seekTimeNs?: string;
  onCursorChange?: (cursor: RerunCursor) => void;
}

export function RerunViewer({ rrdUrl, mobileRrdUrl, rerunVersion, viewerVersion, timeline, seekTimeNs, onCursorChange }: RerunViewerProps) {
  const frame = useRef<HTMLDivElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<WebViewer | null>(null);
  const recordingIdRef = useRef("");
  const onCursorChangeRef = useRef(onCursorChange);
  onCursorChangeRef.current = onCursorChange;
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [nativeFullscreen, setNativeFullscreen] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const mobileCompatibilityMode = isMobileOperatingSystem();
  const effectiveRrdUrl = mobileCompatibilityMode ? mobileRrdUrl : rrdUrl;
  const supportsNativeFullscreen = typeof Element.prototype.requestFullscreen === "function";
  const expanded = nativeFullscreen || focusMode;

  useEffect(() => {
    const syncFullscreen = () => setNativeFullscreen(document.fullscreenElement === frame.current);
    document.addEventListener("fullscreenchange", syncFullscreen);
    return () => document.removeEventListener("fullscreenchange", syncFullscreen);
  }, []);

  useEffect(() => {
    if (!focusMode) return;
    document.body.classList.add("viewer-focus-active");
    const exitOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFocusMode(false);
    };
    document.addEventListener("keydown", exitOnEscape);
    return () => {
      document.body.classList.remove("viewer-focus-active");
      document.removeEventListener("keydown", exitOnEscape);
    };
  }, [focusMode]);

  useEffect(() => {
    let viewer: WebViewer | null = null;
    let removers: Array<() => void> = [];
    let disposed = false;
    setError("");
    setLoading(true);
    if (!effectiveRrdUrl) {
      setError("移动端回放副本暂不可用，请刷新重试或使用电脑查看。");
      setLoading(false);
      return;
    }
    const urlError = validateRrdUrl(effectiveRrdUrl, rerunVersion, viewerVersion || RERUN_VIEWER_VERSION);
    if (urlError) { setError(urlError); setLoading(false); return; }

    void import("@rerun-io/web-viewer").then(async ({ WebViewer: Viewer }) => {
      if (disposed || !container.current) return;
      viewer = new Viewer();
      viewerRef.current = viewer;
      removers = bindCursorEvents(viewer, timeline, recordingIdRef, (cursor) => onCursorChangeRef.current?.(cursor));
      if (mobileCompatibilityMode) {
        const activeViewer = viewer;
        removers.push(viewer.on("recording_open", (event) => activeViewer.set_playing(event.recording_id, false)));
        const pauseWhenHidden = () => {
          if (document.hidden && recordingIdRef.current) activeViewer.set_playing(recordingIdRef.current, false);
        };
        document.addEventListener("visibilitychange", pauseWhenHidden);
        removers.push(() => document.removeEventListener("visibilitychange", pauseWhenHidden));
      }
      const canvasHost = container.current;
      const onContextLost = () => {
        if (disposed) return;
        setError("图形资源已释放，请刷新重试。移动端请避免同时打开多个回放页面。");
        setLoading(false);
        viewer?.stop();
      };
      canvasHost.addEventListener("webglcontextlost", onContextLost, true);
      removers.push(() => canvasHost.removeEventListener("webglcontextlost", onContextLost, true));
      await viewer.start(new URL(effectiveRrdUrl, window.location.origin).href, container.current, {
        width: "100%",
        height: "100%",
        hide_welcome_screen: true,
        allow_fullscreen: false,
        ...(mobileCompatibilityMode ? { render_backend: "webgl" as const } : {}),
      });
      if (!disposed) setLoading(false);
    }).catch((reason: unknown) => {
      viewer?.stop();
      if (!disposed) { setError(reason instanceof Error ? reason.message : "Rerun Viewer 启动失败"); setLoading(false); }
    });

    return () => {
      disposed = true;
      removers.forEach((remove) => remove());
      viewerRef.current = null;
      recordingIdRef.current = "";
      viewer?.stop();
    };
  }, [effectiveRrdUrl, rerunVersion, viewerVersion, timeline, mobileCompatibilityMode]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !recordingIdRef.current || !timeline || !seekTimeNs) return;
    viewer.set_active_timeline(recordingIdRef.current, timeline);
    viewer.set_current_time(recordingIdRef.current, timeline, Number(BigInt(seekTimeNs)));
  }, [seekTimeNs, timeline]);

  async function toggleFullscreen() {
    if (!frame.current) return;
    if (focusMode) {
      setFocusMode(false);
      return;
    }
    try {
      if (document.fullscreenElement === frame.current) await document.exitFullscreen();
      else if (typeof frame.current.requestFullscreen === "function") await frame.current.requestFullscreen();
      else setFocusMode(true);
    } catch {
      setFocusMode(true);
    }
  }

  return (
    <div
      ref={frame}
      className={`viewer-frame${focusMode ? " viewer-focus-mode" : ""}`}
      data-render-backend={mobileCompatibilityMode ? "webgl" : "auto"}
    >
      {loading && <div className="viewer-overlay"><LoadingState label="正在启动 Rerun Viewer…" /></div>}
      {error && <div className="viewer-overlay"><ErrorState message={error} /></div>}
      {!loading && !error && (
        <button
          className="rerun-fullscreen-button"
          type="button"
          aria-label={expanded ? "退出专注回放" : supportsNativeFullscreen ? "全屏查看" : "专注回放"}
          title={expanded ? "退出专注回放" : supportsNativeFullscreen ? "全屏查看" : "专注回放"}
          aria-pressed={expanded}
          onClick={() => void toggleFullscreen()}
        >
          {expanded ? "退出" : supportsNativeFullscreen ? "全屏" : "专注回放"}
        </button>
      )}
      <div ref={container} className="rerun-canvas" data-testid="rerun-viewer" />
    </div>
  );
}

function bindCursorEvents(
  viewer: WebViewer,
  preferredTimeline: string | undefined,
  recordingIdRef: MutableRefObject<string>,
  onCursorChange: RerunViewerProps["onCursorChange"],
): Array<() => void> {
  const publish = (recordingId: string, activeTimeline: string, time: number) => {
    if (!onCursorChange || !activeTimeline || !Number.isFinite(time)) return;
    onCursorChange({ recordingId, timeline: activeTimeline, timeNs: BigInt(Math.round(time)).toString() });
  };
  const onOpen = (event: RecordingOpenEvent) => {
    recordingIdRef.current = event.recording_id;
    const active = preferredTimeline || viewer.get_active_timeline(event.recording_id) || "";
    if (preferredTimeline) viewer.set_active_timeline(event.recording_id, preferredTimeline);
    publish(event.recording_id, active, viewer.get_current_time(event.recording_id, active));
  };
  const onTimeline = (event: TimelineChangeEvent) => publish(event.recording_id, event.timeline, event.time);
  const onTime = (event: TimeUpdateEvent) => {
    const active = viewer.get_active_timeline(event.recording_id) || preferredTimeline || "";
    publish(event.recording_id, active, event.time);
  };
  return [viewer.on("recording_open", onOpen), viewer.on("timeline_change", onTimeline), viewer.on("time_update", onTime)];
}

function validateRrdUrl(value: string, version: string, viewerVersion: string): string {
  if (viewerVersion !== RERUN_VIEWER_VERSION) return `后端 Viewer ${viewerVersion} 与前端固定 Viewer ${RERUN_VIEWER_VERSION} 不一致`;
  if (!isCompatiblePatch(version, viewerVersion)) return `录制版本 ${version} 与固定 Viewer ${viewerVersion} 不兼容`;
  let url: URL;
  try { url = new URL(value, window.location.origin); } catch { return "后端返回了无效的 RRD 地址"; }
  if (!isAllowedProtocol(url)) return "RRD 地址必须使用 HTTPS";
  const configured = (import.meta.env.VITE_RRD_ALLOWED_ORIGINS ?? "").split(",").map((item) => item.trim()).filter(Boolean);
  const allowed = new Set([window.location.origin, ...configured]);
  return allowed.has(url.origin) ? "" : `RRD 地址来源未获准：${url.origin}`;
}

function isCompatiblePatch(recording: string, viewer: string): boolean {
  const pattern = /^(0|[1-9][0-9]*)\.(0|[1-9][0-9]*)\.(0|[1-9][0-9]*)$/;
  const recordingParts = recording.match(pattern)?.slice(1).map(Number);
  const viewerParts = viewer.match(pattern)?.slice(1).map(Number);
  return Boolean(
    recordingParts
    && viewerParts
    && recordingParts[0] === viewerParts[0]
    && recordingParts[1] === viewerParts[1]
    && recordingParts[2] <= viewerParts[2],
  );
}

function isAllowedProtocol(url: URL): boolean {
  if (url.protocol === "https:") return true;
  return url.protocol === "http:" && ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname);
}
